import React from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { GalleryControls } from '@/libraries/design/components/gallery-controls/src/gallery-controls';
import { wacToken } from '@/libraries/design/foundations';

import { NavigationTabsLink } from './navigation-tabs-link';
import { useNavigationTabsScroll } from './use-navigation-tabs-scroll';

import type {
	NavigationTabDataType,
	NavigationTabsAppearance,
	NavigationTabsLayout,
} from './navigation-tabs-section';

const navigationCss = css({ width: '100%', maxWidth: '100%', minWidth: 0 });
const headerCss = css({
	width: '100%',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	marginBlockEnd: '20px',
});
const eyebrowCss = css({
	marginBlock: 0,
	marginInlineEnd: 'auto',
	color: wacToken('color.primary.black'),
	fontFeatureSettings: "'liga' off, 'clig' off",
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '14px',
	fontStyle: 'normal',
	fontWeight: 400,
	lineHeight: '18px',
	textAlign: 'left',
	textTransform: 'uppercase',
});
const controlsCss = css({ display: 'flex', marginInlineStart: 'auto' });
const viewportCss = css({
	width: '100%',
	maxWidth: '100%',
	minWidth: 0,
	overflowX: 'auto',
	['@supports (scrollbar-width: none)']: {
		scrollbarWidth: 'none',
	},
	[`&::-webkit-scrollbar`]: {
		display: 'none',
	},
});
const listCss = css({ display: 'flex', listStyle: 'none', padding: 0, margin: 0, gap: '8px' });
const stackedListCss = css({ width: '100%', flexWrap: 'wrap-reverse' });
const wideStackedListCss = css({ justifyContent: 'center' });
const overflowListCss = css({ width: 'max-content', flexWrap: 'nowrap' });
const itemCss = css({ flexShrink: 0 });

export type NavigationTabsListProps = {
	tabsData: NavigationTabDataType[];
	appearance: NavigationTabsAppearance;
	layout: NavigationTabsLayout;
	eyebrow?: string;
	showControls: boolean;
	activeHref?: string;
	onLinkClick?: (event: React.MouseEvent<HTMLAnchorElement>, href: string) => void;
};

export const NavigationTabsList = ({
	tabsData,
	appearance,
	layout,
	eyebrow,
	showControls,
	activeHref,
	onLinkClick,
}: NavigationTabsListProps) => {
	const intl = useIntl();
	const scroll = useNavigationTabsScroll();
	const controlDistance = scroll.viewportRef.current?.clientWidth ?? 300;
	return (
		<nav
			css={navigationCss}
			aria-label={intl.formatMessage({
				id: 'navigation-tabs.navigation-label',
				defaultMessage: 'Navigation tabs',
			})}
		>
			{(eyebrow || showControls) && (
				<div css={headerCss} data-testid="navigation-tabs-header">
					{eyebrow && (
						<p css={eyebrowCss} data-testid="navigation-tabs-eyebrow">
							{eyebrow}
						</p>
					)}
					{showControls && (
						<div css={controlsCss} data-testid="navigation-tabs-controls">
							<GalleryControls
								iconAppearance="chevron"
								onPrevious={() => scroll.scrollBy(-controlDistance)}
								onNext={() => scroll.scrollBy(controlDistance)}
								isFirstItemVisible={!scroll.scrollState.canScrollPrevious}
								isLastItemVisible={!scroll.scrollState.canScrollNext}
							/>
						</div>
					)}
				</div>
			)}
			<div
				ref={scroll.viewportRef}
				css={viewportCss}
				data-testid="navigation-tabs-viewport"
				onPointerDown={scroll.onPointerDown}
				onPointerMove={scroll.onPointerMove}
				onPointerUp={scroll.onPointerEnd}
				onPointerCancel={scroll.onPointerCancel}
				onLostPointerCapture={scroll.onLostPointerCapture}
				onClickCapture={scroll.onClickCapture}
			>
				<ul
					ref={scroll.listRef}
					css={[
						listCss,
						layout === 'stacked' ? stackedListCss : overflowListCss,
						appearance === 'wide' && layout === 'stacked' && wideStackedListCss,
					]}
					data-layout={layout}
				>
					{tabsData.map((tab) => (
						<li key={tab.id} css={itemCss}>
							<NavigationTabsLink
								{...tab}
								appearance={appearance}
								isActive={activeHref === tab.href}
								onClick={onLinkClick ? (event) => onLinkClick(event, tab.href) : undefined}
							/>
						</li>
					))}
				</ul>
			</div>
		</nav>
	);
};
